import clsx from "clsx";
import { graphql } from "gatsby";
import React, { DetailedHTMLProps, FC, HTMLAttributes, useEffect, useMemo, useState } from "react";
import { usePrevious } from "react-use";

import { useBreakpoints } from "../../hooks/use-breakpoints";
import { CommonColors } from "../../types/colors";
import { PaginationIndicator } from "../pagination/pagination-indicator";
import { HighlightItem } from "./highlight-item";
import { colorScheme } from "./styles";

export interface HighlightsProps
  extends Queries.HighlightsDataFragment,
    Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "className"> {}

export const HighlightsInner: FC<HighlightsProps> = ({
  items,
  color,
  maxVisibleItems,
  className,
}) => {
  const breakpoints = useBreakpoints();

  const [pageIndex, setPageIndex] = useState(0);
  const previousPage = usePrevious(pageIndex);

  const visibleCards = useMemo(() => {
    let responsiveMaxVisibleCards = 1;
    if (breakpoints.sm) responsiveMaxVisibleCards = 2;
    if (breakpoints.lg) responsiveMaxVisibleCards = 3;
    if (breakpoints["2xl"]) responsiveMaxVisibleCards = 4;

    if (maxVisibleItems === null) return responsiveMaxVisibleCards;
    const maxItems = Math.min(responsiveMaxVisibleCards, maxVisibleItems);
    if (Number.isNaN(maxItems)) return responsiveMaxVisibleCards;
    return maxItems;
  }, [breakpoints, maxVisibleItems]);

  const pagesCount = Math.ceil((items?.length ?? 0) / visibleCards);

  const itemsLeftOffsetPercent = useMemo(() => {
    const endIndexForCurrentPage = Math.min((pageIndex + 1) * visibleCards, items?.length ?? 0);
    const startIndexForCurrentPage = endIndexForCurrentPage - visibleCards;
    if (!items?.length) return 0;

    return startIndexForCurrentPage * (100 / visibleCards);
  }, [pageIndex, visibleCards, items]);

  const { containerClassName, titleClassName, descriptionClassName, indicatorColor } =
    colorScheme[(color as CommonColors) ?? CommonColors.Neutral];

  useEffect(() => {
    setPageIndex((currentIndex) => (currentIndex > pagesCount - 1 ? pagesCount - 1 : currentIndex));
  }, [pagesCount]);

  const transitionDurationSeconds = 0.3 * Math.abs(pageIndex - (previousPage ?? pageIndex));

  return (
    <div
      className={clsx(
        "py-4 rounded-3xl container mx-auto overflow-x-hidden relative",
        containerClassName,
        className
      )}
    >
      <div
        className="transition-all flex py-4"
        style={{
          transitionDuration: `${transitionDurationSeconds}s`,
          transform: `translateX(-${itemsLeftOffsetPercent}%)`,
        }}
      >
        {(items ?? []) //
          .map((item, i) => (
            <HighlightItem
              className="px-10"
              style={{
                flex: `0 0 ${100 / visibleCards}%`,
              }}
              {...item!}
              {...{ titleClassName, descriptionClassName }}
              key={i}
            />
          ))}
      </div>

      {pagesCount > 1 && (
        <PaginationIndicator
          className="mt-2 px-2 w-full"
          color={indicatorColor}
          totalItems={pagesCount}
          activeItem={pageIndex}
          onClickItem={setPageIndex}
        />
      )}
    </div>
  );
};

export const Highlights: FC<HighlightsProps> = (props) => {
  const { xs } = useBreakpoints();
  if (!xs) return null;

  return <HighlightsInner {...props} />;
};

export const query = graphql`
  fragment HighlightsData on ContentfulHighlights {
    maxVisibleItems
    color
    items {
      ...HighlightItemData
    }
  }
`;
